import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Barcode, Minus, Plus, Trash2, X, User, CreditCard, Banknote, ArrowRight, Check, Loader2, ArrowLeft, Cloud, History, ArrowDownToLine, Save, Download, LogOut, Weight, Smartphone, Keyboard, Grid, MessageSquare } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';
import api from '@/lib/axios';
import { productosService, categoriasService } from '@/services/productos.service';
import { clientesService } from '@/services/clientes.service';
import { empresasService } from '@/services/empresas.service';
import { ventasService } from '@/services/ventas.service';
import { cajaService } from '@/services/caja.service';
import { CustomerSelectionModal } from './CustomerSelectionModal';
import { LoadSaleModal } from './LoadSaleModal';
import { SalesHistoryModal } from './SalesHistoryModal';
import { CashRegisterModal } from './CashRegisterModal';
import { WithdrawalModal } from './WithdrawalModal';
import { PaymentModal, type PaymentEntry } from './PaymentModal';
import { RechargeModal } from './RechargeModal';
import { SaleTicket } from '@/components/SaleTicket';
import { AdminAuthModal } from '@/components/AdminAuthModal';
import { dialogs } from '@/stores/dialog-store';
import { useReactToPrint } from 'react-to-print';
import { promotionsService, AppliedPromotion } from '@/services/promotions.service';
import { creditNotesService } from '@/services/credit-notes.service';

interface CartItem {
  productId: string;
  name: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  pricePublic: number;
  priceWholesale?: number;
  wholesaleMinQuantity?: number;
  discount: number;
  subtotal: number;
  stock: number;
  image?: string;
  pricesIncludeTax?: boolean;
  productTaxes?: any[];
  unitName?: string;      // Para mostrar unidad (kg, pz, L)
  isWeighable?: boolean;  // Producto a granel (pesable)
  categoryId?: string;    // Para cálculo de promociones
  brandId?: string;       // Para cálculo de promociones
}

interface Customer {
  id: string;
  name: string;
  phone?: string;
  // Datos fiscales (CFDI 4.0) devueltos por el API
  email?: string | null;
  rfc?: string | null;
  taxRegime?: string | null;
  taxZipCode?: string | null;
  cfdiUse?: string | null;
}

interface CartSession {
  id: string;
  name: string;
  items: CartItem[];
  selectedCustomer: Customer | null;
  isQuotation: boolean;
  isRecurring: boolean;
  appliedPromotions: AppliedPromotion[];
}

export function PosPage() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { handleAuthRedirect } = useApiError();
  
  // Tab System State
  const [carts, setCarts] = useState<CartSession[]>([
    { id: '1', name: 'CLIENTE 1', items: [], selectedCustomer: null, isQuotation: false, isRecurring: false, appliedPromotions: [] }
  ]);
  const [activeCartId, setActiveCartId] = useState('1');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [feedbackMsg, setFeedbackMsg] = useState<{text: string, type: 'success'|'error'} | null>(null);

  const showFeedback = (text: string, type: 'success'|'error' = 'success') => {
    setFeedbackMsg({ text, type });
    setTimeout(() => setFeedbackMsg(null), 2500);
  };
  
  const activeCart = carts.find(c => c.id === activeCartId) || carts[0];
  const activeItems = activeCart.items;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSelectedIndex, setSearchSelectedIndex] = useState(0);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cashRegister, setCashRegister] = useState<any>(null);
  
  // Modals state
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [editingQtyId, setEditingQtyId] = useState<string | null>(null);
  const [editingQtyValue, setEditingQtyValue] = useState<string>('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [showCashRegisterModal, setShowCashRegisterModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [showAdminAuthModal, setShowAdminAuthModal] = useState(false);
  const [editingDiscountId, setEditingDiscountId] = useState<string | null>(null);
  const [editingDiscountValue, setEditingDiscountValue] = useState<string>('');
  const [pendingDiscountProductId, setPendingDiscountProductId] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [defaultCustomer, setDefaultCustomer] = useState<Customer | null>(null);
  
  // Estado de replicación al VPS (pendiente de implementar el sync)
  // Inicia desconectado porque no hay VPS configurado aún.
  const [cloudConnected] = useState<boolean>(false);

  // Báscula
  const [scaleWeight, setScaleWeight] = useState<number | null>(null);
  const [bridgeConnected, setBridgeConnected] = useState(false);  // WebSocket conectado al bridge
  const [scaleConnected, setScaleConnected] = useState(false);    // Báscula física conectada
  const [scalePort, setScalePort] = useState<string>('—');
  const [scalePorts, setScalePorts] = useState<{path: string; manufacturer: string | null; friendlyName: string}[]>([]);
  const [showScaleConfig, setShowScaleConfig] = useState(false);
  const [awaitingScale, setAwaitingScale] = useState<string | null>(null);
  const [manualWeightMode, setManualWeightMode] = useState(false);
  const [manualWeight, setManualWeight] = useState('');
  const awaitingScaleRef = useRef<string | null>(null);
  const pendingProductRef = useRef<any>(null);
  const productsRef = useRef<any[]>([]);
  const wsRef = useRef<WebSocket | null>(null);

  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const barcodeTimer = useRef<any>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ── Product grid view state ──
  const [showProductGrid, setShowProductGrid] = useState(false);
  const [gridCategoryId, setGridCategoryId] = useState<string>('');
  const [gridProducts, setGridProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [loadingGrid, setLoadingGrid] = useState(false);

  // Promotions state (per cart)
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const [promotedProductIds, setPromotedProductIds] = useState<Set<string>>(new Set());
  const [productPromotions, setProductPromotions] = useState<Record<string, {
    promotionId: string;
    promotionName: string;
    promotionType: string;
    description: string;
  }[]>>({});
  const promotionTimerRef = useRef<any>(null);

  // Confirm before closing browser tab if cart has items or caja is open
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (activeItems.length > 0 || cashRegister?.status === 'OPEN') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [activeItems.length, cashRegister?.status]);

  // Promo sound & animation feedback
  const audioCtxRef = useRef<AudioContext | null>(null);
  const [promoFlashProductId, setPromoFlashProductId] = useState<string | null>(null);
  const [showPromoToast, setShowPromoToast] = useState(false);
  const [promoToastName, setPromoToastName] = useState('');
  const promoFlashTimerRef = useRef<any>(null);

  const playPromotionSound = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;
      
      // Create a pleasant two-tone chime: C5 → E5 (major third)
      const oscillator1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      oscillator1.type = 'sine';
      oscillator1.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
      gain1.gain.setValueAtTime(0.15, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      oscillator1.connect(gain1);
      gain1.connect(ctx.destination);
      oscillator1.start(ctx.currentTime);
      oscillator1.stop(ctx.currentTime + 0.3);

      const oscillator2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      oscillator2.type = 'sine';
      oscillator2.frequency.setValueAtTime(659.25, ctx.currentTime + 0.08); // E5
      gain2.gain.setValueAtTime(0.12, ctx.currentTime + 0.08);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      oscillator2.connect(gain2);
      gain2.connect(ctx.destination);
      oscillator2.start(ctx.currentTime + 0.08);
      oscillator2.stop(ctx.currentTime + 0.4);
    } catch (err) {
      console.warn('[POS] Audio no disponible:', err);
    }
  }, []);

  // Flash animation on the item row when a promoted product is added
  const triggerPromoFlash = useCallback((productId: string, productName: string, promoDescription?: string) => {
    if (promoFlashTimerRef.current) clearTimeout(promoFlashTimerRef.current);
    
    setPromoFlashProductId(productId);
    setPromoToastName(promoDescription ? `${productName} — ${promoDescription}` : productName);
    setShowPromoToast(true);
    
    promoFlashTimerRef.current = setTimeout(() => {
      setPromoFlashProductId(null);
      setShowPromoToast(false);
    }, 5000);
  }, []);

  // Payment form
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'CARD' | 'TRANSFER' | 'CREDIT'>('CASH');
  const [cashReceived, setCashReceived] = useState('');
  const [cardReference, setCardReference] = useState('');
  const [saleNotes, setSaleNotes] = useState('');
  const [saleResult, setSaleResult] = useState<any>(null);
  
  const printRef = useRef<HTMLDivElement>(null);
  const [saleToPrint, setSaleToPrint] = useState<any>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: () => setSaleToPrint(null),
  });

  useEffect(() => {
    if (saleToPrint) {
      setTimeout(() => {
        handlePrint();
      }, 100);
    }
  }, [saleToPrint, handlePrint]);

  // Load products based on search term
  const loadProducts = useCallback(async () => {
    if (!user?.company?.id || searchTerm.length < 1) {
      setProducts([]);
      return;
    }
    try {
      const result = await productosService.findAll({
        companyId: user.company.id,
        search: searchTerm,
        limit: 10,
      });
      setProducts(result.data || []);
    } catch (err) {
      console.warn('Error loading products:', err);
    }
  }, [user, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts();
    }, 300);
    return () => clearTimeout(timer);
  }, [loadProducts]);

  // ── Load products for grid view ──
  const loadGridProducts = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoadingGrid(true);
    try {
      const result = await productosService.findAll({
        companyId: user.company.id,
        limit: 50,
        categoryId: gridCategoryId || undefined,
      });
      setGridProducts(result.data || []);
    } catch (err) {
      console.warn('Error loading grid products:', err);
    }
    setLoadingGrid(false);
  }, [user, gridCategoryId]);

  // Load categories for grid filter
  useEffect(() => {
    if (!user?.company?.id || !showProductGrid) return;
    categoriasService.findAll(user.company.id)
      .then((cats: any[]) => setCategories(cats))
      .catch(() => {});
    loadGridProducts();
  }, [showProductGrid, user?.company?.id, loadGridProducts]);

  // Preload categories on mount for quick access
  useEffect(() => {
    if (!user?.company?.id) return;
    categoriasService.findAll(user.company.id)
      .then((cats: any[]) => setCategories(cats))
      .catch(() => {});
  }, [user?.company?.id]);

  // Mantener refs sincronizados para el WebSocket
  useEffect(() => { awaitingScaleRef.current = awaitingScale; }, [awaitingScale]);
  useEffect(() => { productsRef.current = products; }, [products]);

  // ── WebSocket Báscula (se conecta UNA SOLA VEZ al montar) ──
  useEffect(() => {
    let reconnectTimer: any = null;
    let ws: WebSocket | null = null;

    const connectWs = () => {
      try {
        ws = new WebSocket('ws://localhost:3120');
        wsRef.current = ws;

        ws.onopen = () => {
          setBridgeConnected(true);
          // NO marcar scaleConnected aquí — esperar mensaje 'status' del bridge
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);

            // Mensaje de estado del bridge: esta es la UNICA fuente de verdad
            if (msg.type === 'status' && msg.data) {
              setScaleConnected(msg.data.connected);
              setScalePort(msg.data.comPort || '—');
              setBridgeConnected(true);
              return;
            }

            // Mensaje de peso
            if (msg.type === 'weight' && msg.data) {
              setScaleWeight(msg.data.weight);

              // Leer de refs (siempre actualizados, sin causar re-renders ni reconexiones)
              const targetId = awaitingScaleRef.current;
              if (msg.data.stable && msg.data.weight > 0 && targetId) {
                const product = productsRef.current.find((p: any) => p.id === targetId);
                if (product) {
                  addToCart(product, msg.data.weight);
                  setAwaitingScale(null);
                  showFeedback(`⚖️ ${msg.data.weight.toFixed(3)} kg capturado`, 'success');
                }
              }
            }
          } catch (err) {
            console.warn('[POS] Error al procesar mensaje WebSocket:', err);
          }
        };

        ws.onclose = () => {
          setBridgeConnected(false);
          setScaleConnected(false);
          reconnectTimer = setTimeout(connectWs, 3000);
        };

        ws.onerror = () => {
          setBridgeConnected(false);
          setScaleConnected(false);
        };
      } catch (err) {
        console.warn('[POS] Error al conectar WebSocket báscula:', err);
        reconnectTimer = setTimeout(connectWs, 5000);
      }
    };

    connectWs();

    return () => {
      if (ws) ws.close();
      wsRef.current = null;
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, []);

  // ── Fetch puertos COM disponibles desde el bridge ──
  const fetchPorts = useCallback(async () => {
    try {
      const resp = await fetch('http://localhost:3120/api/ports');
      const data = await resp.json();
      if (data.ports) setScalePorts(data.ports);
    } catch (err) {
      console.warn('[POS] Error al obtener puertos de báscula:', err);
      setScalePorts([]);
    }
  }, []);

  // ── Conectar a un puerto COM específico ──
  const connectToPort = async (comPort: string) => {
    try {
      await fetch('http://localhost:3120/api/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comPort }),
      });
      setScalePort(comPort);
      setShowScaleConfig(false);
      showFeedback(`🔌 Conectando a ${comPort}...`, 'success');
    } catch (err) {
      console.warn('[POS] Error al conectar con la báscula:', err);
      showFeedback('❌ Error al conectar con la báscula', 'error');
    }
  };

  const loadCajaStatus = useCallback(() => {
    if (user?.branch?.id) {
      cajaService.getStatus(user.branch.id, user.id).then(setCashRegister).catch((err) => {
        console.warn('[POS] Error al cargar estado de caja:', err);
      });
    }
  }, [user]);

  useEffect(() => {
    loadCajaStatus();
  }, [loadCajaStatus]);

  // Load products with active promotions for badge display
  useEffect(() => {
    if (!user?.company?.id) return;
    promotionsService.getActiveProductIds(user.company.id, user.branch?.id)
      .then(result => {
        setPromotedProductIds(new Set(result.productIds));
        setProductPromotions(result.productPromotions || {});
      })
      .catch((err) => {
        console.warn('[POS] Error al cargar promociones activas:', err);
      });
  }, [user?.company?.id, user?.branch?.id]);

  // Cleanup AudioContext on unmount
  useEffect(() => {
    return () => {
      audioCtxRef.current?.close();
    };
  }, []);

  // Load default customer (Público en General) and pre-assign to carts
  useEffect(() => {
    const loadDefaultCustomer = async () => {
      if (!user?.company?.id) return;
      try {
        const raw: any = await clientesService.findAll(user.company.id, 'Público');
        const list: any[] = Array.isArray(raw) ? raw : (raw?.data || []);
        let found = list.find((c: any) => {
          const n = String(c.name || '').toLowerCase();
          return n.includes('público en general') || n.includes('publico en general') || n.includes('mostrador');
        });
        // If not found, create it automatically (backend evita duplicados)
        if (!found) {
          found = await clientesService.create({
            companyId: user.company.id,
            name: 'Público en General',
          });
        }
        if (found) {
          setDefaultCustomer(found);
          setCarts(prev => prev.map(cart =>
            cart.selectedCustomer ? cart : { ...cart, selectedCustomer: found }
          ));
        }
      } catch (err) {
        console.warn('[POS] Error al cargar cliente por defecto:', err);
      }
    };
    loadDefaultCustomer();
  }, [user]);

  // Tab Management
  const addNewTab = () => {
    const newId = Date.now().toString();
    const newTabCount = carts.length + 1;
    setCarts([...carts, { id: newId, name: `CLIENTE ${newTabCount}`, items: [], selectedCustomer: defaultCustomer, isQuotation: false, isRecurring: false, appliedPromotions: [] }]);
    setActiveCartId(newId);
  };

  const closeTab = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (carts.length === 1) return; // Prevent closing last tab
    const newCarts = carts.filter(c => c.id !== id);
    setCarts(newCarts);
    if (activeCartId === id) setActiveCartId(newCarts[0].id);
  };

  const updateActiveCart = (updates: Partial<CartSession>) => {
    setCarts(carts.map(c => c.id === activeCartId ? { ...c, ...updates } : c));
  };

  const handleLoadSale = (saleId: string, mappedItems: CartItem[], customer: Customer | null) => {
    updateActiveCart({
      items: mappedItems,
      selectedCustomer: customer,
      isQuotation: false,
      isRecurring: false
    });
    setShowLoadModal(false);
  };

  const POS_SHORTCUTS = [
    { key: 'F6', desc: 'Guardar cotización' },
    { key: 'F7', desc: 'Ventas del día' },
    { key: 'F8', desc: 'Retiros del día' },
    { key: 'F9', desc: 'Alternar precio mayoreo' },
    { key: 'F11', desc: 'Cargar venta guardada' },
    { key: 'Ctrl+K', desc: 'Mostrar esta ayuda' },
  ];

  // Keyboard Shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K - Mostrar ayuda de atajos
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowShortcutsModal(true);
        return;
      }
      // Escape - Cerrar modal de atajos
      if (e.key === 'Escape' && showShortcutsModal) {
        setShowShortcutsModal(false);
        return;
      }
      // F7 - Ventas del Dia
      if (e.key === 'F7') {
        e.preventDefault();
        setShowSalesModal(true);
      }
      // F8 - Retiros del Dia
      if (e.key === 'F8') {
        e.preventDefault();
        setShowWithdrawalModal(true);
      }
      // F6 - Guardar Cotización/Pendiente
      if (e.key === 'F6') {
        e.preventDefault();
        handleSaveQuotation();
      }
      // F11 - Cargar
      if (e.key === 'F11') {
        e.preventDefault();
        setShowLoadModal(true);
      }
      // F9 - Mayoreo Manual (Toggle)
      if (e.key === 'F9') {
        e.preventDefault();
        if (activeItems.length > 0) {
          const targetId = selectedProductId || activeItems[0].productId;
          let action: string = 'error';
          let productName = '';
          let requiredMin = 0;
          
          updateActiveCart({
            items: activeItems.map(item => {
              if (item.productId === targetId) {
                productName = item.name;
                requiredMin = item.wholesaleMinQuantity || 0;
                if (item.priceWholesale && item.priceWholesale > 0) {
                  // Si actualmente tiene precio de mayoreo, lo regresamos a público
                  if (item.unitPrice === item.priceWholesale) {
                    action = 'removed';
                    return { 
                      ...item, 
                      unitPrice: item.pricePublic, 
                      subtotal: item.quantity * item.pricePublic 
                    };
                  } 
                  // Si tiene precio público, revisamos cantidad y aplicamos
                  else {
                    if (item.wholesaleMinQuantity && item.quantity < item.wholesaleMinQuantity) {
                      action = 'min_not_met';
                      return item;
                    }
                    action = 'applied';
                    return { 
                      ...item, 
                      unitPrice: item.priceWholesale, 
                      subtotal: item.quantity * item.priceWholesale 
                    };
                  }
                }
              }
              return item;
            })
          });
          
          if (action === 'applied') {
            showFeedback(`Precio mayoreo APLICADO a: ${productName}`, 'success');
          } else if (action === 'removed') {
            showFeedback(`Precio mayoreo REMOVIDO de: ${productName}`, 'success');
          } else if (action === 'min_not_met') {
            showFeedback(`No se puede aplicar mayoreo a ${productName}. Mínimo requerido: ${requiredMin} piezas.`, 'error');
          } else {
            showFeedback('El producto no tiene precio de mayoreo configurado', 'error');
          }
        } else {
          showFeedback('El carrito está vacío', 'error');
        }
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [activeCart, selectedProductId, showShortcutsModal]);

  // Barcode scanner support
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // Ignore if typing in an input other than the main search
      if (e.target instanceof HTMLInputElement && e.target !== searchInputRef.current) return;
      
      if (e.key === 'Enter' && barcodeBuffer.length > 3) {
        handleBarcodeSearch(barcodeBuffer);
        setBarcodeBuffer('');
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        setBarcodeBuffer(prev => prev + e.key);
        clearTimeout(barcodeTimer.current);
        barcodeTimer.current = setTimeout(() => setBarcodeBuffer(''), 200);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [barcodeBuffer]);

  const handleBarcodeSearch = async (barcode: string) => {
    if (!user?.company?.id) return;
    try {
      // First try exact barcode match
      const product = await productosService.findByBarcode(barcode, user.company.id);
      addToCart(product);  // addToCart already handles promo sound + flash
      setSearchTerm('');
      setProducts([]);
    } catch (err) { 
      console.warn('[POS] Código de barras no encontrado:', err);
    }
  };

  const addToCart = async (product: any, scaleWeight?: number) => {
    // Si es producto a granel y no viene con peso de báscula, esperar báscula
    if (scaleWeight === undefined && (product.isWeighable || product.unit?.name === 'Kilogramo' || product.unit?.name === 'Litro')) {
      setAwaitingScale(product.id);
      pendingProductRef.current = product;
      setManualWeightMode(false);
      setManualWeight('');
      showFeedback('⚖️ Coloca el producto en la báscula', 'success');
      return;
    }

    const qty = scaleWeight !== undefined ? scaleWeight : 1;

    const existing = activeItems.find(item => item.productId === product.id);
    let newItems;
    if (existing) {
      // Si es producto por peso, no se puede incrementar — se reemplaza
      if (product.isWeighable && scaleWeight) {
        const updatedItem = { ...existing, quantity: scaleWeight, subtotal: scaleWeight * existing.unitPrice };
        updateActiveCart({ items: [updatedItem, ...activeItems.filter(item => item.productId !== product.id)] });
        setSelectedProductId(product.id);
        return;
      }
      const newQty = existing.quantity + 1;
      
      // Stock validation
      if (user?.company?.allowNegativeStock === false && existing.stock !== undefined) {
        if (newQty > existing.stock) {
          showFeedback(`Stock insuficiente para ${product.name}. Disponible: ${existing.stock}`, 'error');
          return;
        }
      }

      let newPrice = existing.unitPrice;
      // Auto apply wholesale if reached
      if (existing.wholesaleMinQuantity && newQty >= existing.wholesaleMinQuantity && existing.priceWholesale) {
        newPrice = existing.priceWholesale;
      }
      
      const updatedItem = { 
        ...existing, 
        quantity: newQty, 
        unitPrice: newPrice,
        subtotal: newQty * newPrice 
      };
      
      // Move to top
      newItems = [updatedItem, ...activeItems.filter(item => item.productId !== product.id)];
    } else {
      // Stock validation for new item
      if (user?.company?.allowNegativeStock === false && product.stock !== undefined) {
        if (1 > product.stock) {
          showFeedback(`Stock insuficiente para ${product.name}. Disponible: ${product.stock}`, 'error');
          return;
        }
      }

      let initialPrice = Number(product.pricePublic);
      // Auto apply wholesale if min quantity is 1
      if (product.wholesaleMinQuantity === 1 && product.priceWholesale) {
        initialPrice = Number(product.priceWholesale);
      }
      
      newItems = [{
        productId: product.id,
        name: product.name,
        sku: product.sku,
        quantity: qty,
        unitPrice: initialPrice,
        pricePublic: Number(product.pricePublic),
        priceWholesale: product.priceWholesale ? Number(product.priceWholesale) : undefined,
        wholesaleMinQuantity: product.wholesaleMinQuantity,
        discount: 0,
        subtotal: initialPrice * qty,
        stock: product.stock,
        pricesIncludeTax: product.pricesIncludeTax !== undefined ? product.pricesIncludeTax : true,
        productTaxes: (product.productTaxes && product.productTaxes.length > 0) ? product.productTaxes : [{ tax: { name: 'IVA 16%', rate: 0.16 } }],
        unitName: product.unit?.name,
        isWeighable: product.isWeighable,
        categoryId: product.categoryId,
        brandId: product.brandId,
      }, ...activeItems];
    }
    updateActiveCart({ items: newItems });
    setSelectedProductId(product.id);
    setSearchTerm('');
    setProducts([]);
    searchInputRef.current?.focus();
    
    // Promo feedback when adding a product that has an active promotion
    if (promotedProductIds.has(product.id)) {
      playPromotionSound();
      const promos = productPromotions[product.id];
      const promoDesc = promos && promos.length > 0 ? promos[0].description : undefined;
      triggerPromoFlash(product.id, product.name, promoDesc);
    }
  };

  // ── Manejar recarga desde el modal ──
  const handleRecharge = (data: { operator: string; operatorName: string; phone: string; amount: number; type: string }) => {
    const rechargeId = `recharge_${Date.now()}`;
    const displayName = data.type === 'AIRTIME'
      ? `📱 Recarga ${data.operatorName} $${data.amount}`
      : `💡 Pago ${data.operatorName} $${data.amount}`;
    const displayDetail = data.type === 'AIRTIME'
      ? `${data.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')}`
      : `Ref: ${data.phone}`;

    const rechargeItem = {
      productId: rechargeId,
      name: displayName,
      sku: `REC-${data.operator}-${data.amount}`,
      quantity: 1,
      unitPrice: data.amount,
      pricePublic: data.amount,
      discount: 0,
      subtotal: data.amount,
      stock: 999,
      unitName: 'pza',
      productTaxes: [],
    };

    updateActiveCart({ items: [rechargeItem, ...activeItems] });
    setSelectedProductId(rechargeId);
    showFeedback(`✅ ${displayName} - ${displayDetail}`, 'success');
  };

  const updateQuantity = (productId: string, delta: number) => {
    updateActiveCart({
      items: activeItems.map(item => {
        if (item.productId === productId) {
          const newQty = Math.max(1, item.quantity + delta);
          
          // Stock validation
          if (user?.company?.allowNegativeStock === false && item.stock !== undefined) {
            if (newQty > item.stock) {
              showFeedback(`Stock insuficiente para ${item.name}. Disponible: ${item.stock}`, 'error');
              return item;
            }
          }

          let newPrice = item.unitPrice;
          
          // Auto wholesale
          if (item.wholesaleMinQuantity) {
            if (newQty >= item.wholesaleMinQuantity && item.priceWholesale) {
              newPrice = item.priceWholesale;
            } else {
              newPrice = item.pricePublic; // Revert to public if below
            }
          }
          
          return { ...item, quantity: newQty, unitPrice: newPrice, subtotal: newQty * newPrice };
        }
        return item;
      }).filter(item => item.quantity > 0)
    });
  };

  const setExactQuantity = (productId: string, exactQty: number) => {
    if (isNaN(exactQty) || exactQty < 1) exactQty = 1;

    updateActiveCart({
      items: activeItems.map(item => {
        if (item.productId === productId) {
          // Stock validation
          if (user?.company?.allowNegativeStock === false && item.stock !== undefined) {
            if (exactQty > item.stock) {
              showFeedback(`Stock insuficiente para ${item.name}. Disponible: ${item.stock}`, 'error');
              return item;
            }
          }

          let newPrice = item.unitPrice;
          
          // Auto wholesale
          if (item.wholesaleMinQuantity) {
            if (exactQty >= item.wholesaleMinQuantity && item.priceWholesale) {
              newPrice = item.priceWholesale;
            } else {
              newPrice = item.pricePublic; // Revert to public if below
            }
          }
          
          return { ...item, quantity: exactQty, unitPrice: newPrice, subtotal: exactQty * newPrice };
        }
        return item;
      })
    });
  };

  const submitQtyEdit = (productId: string) => {
    if (editingQtyValue && !isNaN(Number(editingQtyValue))) {
      setExactQuantity(productId, parseInt(editingQtyValue, 10));
    }
    setEditingQtyId(null);
  };

  const removeFromCart = (productId: string) => {
    updateActiveCart({ items: activeItems.filter(item => item.productId !== productId) });
  };

  const setItemDiscount = (productId: string, discountValue: number) => {
    if (isNaN(discountValue) || discountValue < 0) discountValue = 0;
    
    updateActiveCart({
      items: activeItems.map(item => {
        if (item.productId === productId) {
          const itemSubtotal = item.quantity * item.unitPrice;
          const capped = Math.min(discountValue, itemSubtotal);
          return { ...item, discount: capped, subtotal: Math.max(0, itemSubtotal - capped) };
        }
        return item;
      })
    });
  };

  const handleEditDiscount = (productId: string) => {
    const role = user?.role || '';
    // SUPER_ADMIN, ADMIN, GERENTE_GENERAL apply discount directly
    if (['SUPER_ADMIN', 'ADMIN', 'GERENTE_GENERAL'].includes(role)) {
      const item = activeItems.find(i => i.productId === productId);
      if (item) {
        setEditingDiscountId(productId);
        setEditingDiscountValue(String(item.discount));
      }
    } else {
      // CAJERO or VENDEDOR need admin authorization
      setPendingDiscountProductId(productId);
      setShowAdminAuthModal(true);
    }
  };

  const submitDiscountEdit = (productId: string) => {
    if (editingDiscountValue && !isNaN(Number(editingDiscountValue))) {
      setItemDiscount(productId, parseFloat(editingDiscountValue));
      showFeedback(`Descuento aplicado: $${parseFloat(editingDiscountValue).toFixed(2)}`, 'success');
    }
    setEditingDiscountId(null);
    setEditingDiscountValue('');
  };

  let rawSubtotal = 0;
  let computedTaxTotal = 0;
  let computedDiscountTotal = 0;

  activeItems.forEach(item => {
    const itemSubtotal = item.quantity * item.unitPrice;
    const itemDiscount = item.quantity * item.discount;
    const netTotal = itemSubtotal - itemDiscount;

    let taxRate = 0;
    if (item.productTaxes && item.productTaxes.length > 0) {
      taxRate = item.productTaxes.reduce((sum: number, pt: any) => {
        // Handle both nested tax object and flat rate depending on API response
        const rate = pt.tax?.rate !== undefined ? pt.tax.rate : (pt.rate || 0);
        return sum + Number(rate);
      }, 0);
    }

    if (item.pricesIncludeTax) {
      // The price already includes tax
      const baseAmount = netTotal / (1 + taxRate);
      const taxAmount = netTotal - baseAmount;
      
      // rawSubtotal is the base price (without tax)
      rawSubtotal += baseAmount + itemDiscount;
      computedTaxTotal += taxAmount;
      computedDiscountTotal += itemDiscount;
    } else {
      // The price does not include tax
      const taxAmount = netTotal * taxRate;
      
      rawSubtotal += itemSubtotal;
      computedTaxTotal += taxAmount;
      computedDiscountTotal += itemDiscount;
    }
  });

  // Calculate promotion discounts
  const cartPromotions = activeCart.appliedPromotions || [];
  const promotionDiscount = cartPromotions.reduce((sum, p) => sum + p.discountAmount, 0);

  const subtotal = rawSubtotal;
  const discountTotal = computedDiscountTotal + promotionDiscount;
  const taxTotal = computedTaxTotal;
  const grandTotal = Math.max(0, subtotal - discountTotal + taxTotal);
  const totalItems = activeItems.reduce((sum, item) => sum + item.quantity, 0);

  // ── Calculate promotions whenever cart items change ──
  useEffect(() => {
    if (!user?.company?.id || activeItems.length === 0) {
      updateActiveCart({ appliedPromotions: [] });
      return;
    }

    if (promotionTimerRef.current) clearTimeout(promotionTimerRef.current);
    
    const currentCartId = activeCartId;
    promotionTimerRef.current = setTimeout(async () => {
      setPromotionsLoading(true);
      try {
        const result = await promotionsService.calculate({
          companyId: user.company.id,
          branchId: user.branch?.id,
          customerId: activeCart.selectedCustomer?.id,
          items: activeItems.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            categoryId: item.categoryId,
            brandId: item.brandId,
          })),
        });
        // Only update if we're still on the same cart
        setCarts(prev => prev.map(c => 
          c.id === currentCartId ? { ...c, appliedPromotions: result.appliedPromotions || [] } : c
        ));
      } catch (err) {
        console.warn('[POS] Error al calcular promociones:', err);
        setCarts(prev => prev.map(c => 
          c.id === currentCartId ? { ...c, appliedPromotions: [] } : c
        ));
      }
      setPromotionsLoading(false);
    }, 800);

    return () => {
      if (promotionTimerRef.current) clearTimeout(promotionTimerRef.current);
    };
  }, [activeItems, activeCartId, user?.company?.id, user?.branch?.id, activeCart.selectedCustomer?.id]);

  const handlePayment = async (payments: PaymentEntry[], requestInvoice?: boolean) => {
    if (!user?.company?.id || !user?.branch?.id) return;
    if (activeItems.length === 0) return;
    
    setSaving(true);
    setShowPaymentModal(false);
    
    const hasCash = payments.some(p => p.method === 'CASH');
    const totalCash = payments.filter(p => p.method === 'CASH').reduce((s, p) => s + p.amount, 0);
    const changeAmount = hasCash ? Math.max(0, totalCash - grandTotal) : 0;
    
    try {
      // Nota: NO se envía userId — el backend lo toma del JWT y rechaza campos extra (forbidNonWhitelisted)
      const result = await ventasService.create({
        companyId: user.company.id,
        branchId: user.branch.id,
        cashRegisterId: cashRegister?.id,
        customerId: activeCart.selectedCustomer?.id,
        items: activeItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount,
        })),
        payments: payments.map(p => ({
          method: p.method,
          amount: p.amount,
          reference: p.reference || undefined,
        })),
        notes: saleNotes || undefined,
        registerPrefix: localStorage.getItem('REGISTER_PREFIX') || 'C1',
        subtotal,
        discountTotal,
        taxTotal: Math.round(taxTotal * 100) / 100,
        grandTotal: Math.round(grandTotal * 100) / 100,
        paidAmount: grandTotal,
        changeAmount,
      });

      // Record promotion usage
      const promoIds = cartPromotions.map(p => p.promotionId);
      if (promoIds.length > 0) {
        promotionsService.recordUsage(promoIds).catch((err) => {
          console.warn('[POS] Error al registrar uso de promociones:', err);
        });
      }

      // Apply credit notes if any payment was made with NOTA_CREDITO
      const creditNotePayments = payments.filter(p => p.method === 'NOTA_CREDITO' && p.reference);
      for (const cp of creditNotePayments) {
        try {
          await creditNotesService.useCreditNote(cp.reference, cp.amount);
        } catch (err) {
          console.warn('[POS] Error al aplicar Nota de Crédito:', err);
        }
      }

      if (requestInvoice && result.saleId) {
        try {
          // email solo si existe: el backend valida @IsEmail() y rechaza cadena vacía
          const selectedCustomer = activeCart.selectedCustomer;
          const invResp = await api.post('/invoices', {
            companyId: user.company.id,
            saleId: result.saleId,
            customerName: selectedCustomer?.name || 'PUBLICO EN GENERAL',
            customerRfc: selectedCustomer?.rfc || 'XAXX010101000',
            taxRegime: selectedCustomer?.taxRegime || '616',
            taxZipCode: selectedCustomer?.taxZipCode || '00000',
            cfdiUse: selectedCustomer?.cfdiUse || 'S01',
            email: selectedCustomer?.email || undefined,
          }, { headers: { 'x-bypass-local': 'true' } });
          showFeedback('Venta y factura generadas con éxito', 'success');
          
          if (invResp.data?.pdfUrl) {
            window.open(invResp.data.pdfUrl, '_blank');
          }
        } catch (invErr: any) {
          console.warn('[POS] Error al facturar:', invErr);
          showFeedback('Venta cobrada, pero falló la facturación.', 'error');
        }
      } else {
        showFeedback(activeCart.isQuotation ? 'Cotización guardada' : 'Venta cobrada con éxito', 'success');
      }

      setSaleResult(result);
      setSaleToPrint(result);
      updateActiveCart({ items: [], selectedCustomer: null, isQuotation: false, isRecurring: false, appliedPromotions: [] });
      setSaleNotes('');
    } catch (err: any) {
      await dialogs.alert(err.response?.data?.message || 'Error al procesar la venta');
    }
    setSaving(false);
  };

  const handleSaveQuotation = async () => {
    if (!user?.company?.id || !user?.branch?.id) return;
    if (activeItems.length === 0) return;
    
    setSaving(true);
    try {
      const result = await ventasService.create({
        companyId: user.company.id,
        branchId: user.branch.id,
        cashRegisterId: cashRegister?.id,
        customerId: activeCart.selectedCustomer?.id,
        items: activeItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount,
        })),
        payments: [{ method: 'CASH', amount: 0 }],
        notes: saleNotes || undefined,
        subtotal,
        discountTotal,
        taxTotal: Math.round(taxTotal * 100) / 100,
        grandTotal: Math.round(grandTotal * 100) / 100,
        paidAmount: 0,
        changeAmount: 0,
        status: 'QUOTATION'
      });

      await dialogs.alert('Cotización guardada exitosamente.');
      updateActiveCart({ items: [], selectedCustomer: null, isQuotation: false, isRecurring: false });
      setSaleNotes('');
    } catch (err: any) {
      await dialogs.alert(err.response?.data?.message || 'Error al guardar la cotización');
    }
    setSaving(false);
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col font-sans overflow-hidden relative">
      
      {/* Toast Feedback */}
      {feedbackMsg && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-full shadow-lg text-white font-bold animate-in fade-in slide-in-from-top-4 flex items-center gap-2 ${feedbackMsg.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {feedbackMsg.type === 'success' ? <Check size={20} /> : <X size={20} />}
          {feedbackMsg.text}
        </div>
      )}

      {/* Promo Toast - appears when a promoted product is scanned */}
      {showPromoToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 promo-toast-enter">
          <div className="flex items-center gap-2 px-5 py-2.5 rounded-full shadow-lg bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300">
            <span className="text-lg animate-bounce">⭐</span>
            <span className="text-sm font-black text-amber-800 uppercase tracking-wide">
              {promoToastName}
            </span>
             <span className="text-lg animate-bounce">⭐</span>
          </div>
        </div>
      )}

      {/* Top Bar - Tabs & User Info */}
      <div className="h-14 bg-white border-b border-gray-200 flex items-end justify-between px-2">
        <div className="flex items-end h-full gap-1 overflow-x-auto pb-0">
          {carts.map(cart => (
            <div 
              key={cart.id}
              onClick={() => setActiveCartId(cart.id)}
              className={`group flex items-center gap-2 h-10 px-4 rounded-t-lg border border-b-0 cursor-pointer transition-colors ${
                activeCartId === cart.id 
                  ? 'bg-[#007AFF] text-white border-[#007AFF]' 
                  : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
              }`}
            >
              <ShoppingCart size={16} />
              <span className="text-sm font-bold whitespace-nowrap">{cart.name}</span>
              {carts.length > 1 && (
                <button 
                  onClick={(e) => closeTab(cart.id, e)}
                  className={`p-0.5 rounded-full ${activeCartId === cart.id ? 'hover:bg-blue-600' : 'hover:bg-gray-300'}`}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          <button 
            onClick={addNewTab}
            className="h-10 px-3 flex items-center justify-center text-gray-500 hover:text-[#007AFF] hover:bg-blue-50 rounded-t-lg transition-colors mb-0"
          >
            <Plus size={20} />
          </button>
        </div>
        
        <div className="flex items-center gap-6 h-full pb-2">
          {/* Botón Teclas */}
          <button
            onClick={() => setShowShortcutsModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold border border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-all"
            title="Atajos de teclado"
          >
            <Keyboard size={14} />
            <span>Teclas</span>
            <kbd className="text-[9px] bg-gray-200 px-1 py-0.5 rounded font-mono text-gray-500">Ctrl+K</kbd>
          </button>
          {/* Indicador de replicación al VPS - desconectado hasta que se configure */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${
            cloudConnected
              ? 'bg-green-50 text-green-600 border-green-200'
              : 'bg-gray-50 text-gray-400 border-gray-200'
          }`}>
            <Cloud size={16} className={cloudConnected ? '' : 'opacity-50'} />
            <span className="text-xs font-bold">{cloudConnected ? 'Nube conectada' : 'Sin conexión'}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm">
              <span className="text-gray-500">ATIENDE:</span>{' '}
              <span className="font-bold text-gray-800 uppercase">{user?.fullName || 'CAJERO'}</span>
            </div>
            <button 
              onClick={async () => {
                const confirmed = await dialogs.confirm(
                  '¿Estás seguro de que deseas salir? Tu sesión se cerrará pero tu caja seguirá abierta para el siguiente turno.',
                  { title: 'Cerrar Sesión', confirmText: 'Sí, Cerrar Sesión', cancelText: 'Cancelar' }
                );
                if (confirmed) {
                  logout();
                }
              }} 
              className="text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-colors"
              title="Cerrar Sesión"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Action Bar (Below Tabs) */}
      <div className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          {/* 🚧 Pendiente de ayer — oculto, disponible para implementación futura
          <button className="flex items-center gap-2 bg-yellow-50 text-yellow-700 border border-yellow-200 px-3 py-1.5 rounded-full text-xs font-bold uppercase hover:bg-yellow-100 transition-colors">
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
            Pendiente de ayer
          </button>
          */}
        </div>
        <div className="flex items-center gap-3">
          {/* Toggle Grid / Search */}
          <button
            onClick={() => setShowProductGrid(!showProductGrid)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold border transition-all ${
              showProductGrid
                ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600'
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
            }`}
            title={showProductGrid ? 'Ver búsqueda' : 'Ver productos'}
          >
            <Grid size={14} />
            <span>{showProductGrid ? 'Buscar' : 'Productos'}</span>
          </button>

          {/* Botón Recargas */}
          <button
            onClick={() => setShowRechargeModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-all"
            title="Vender recargas y pagar servicios"
          >
            <Smartphone size={14} />
            <span>Recargas</span>
          </button>

          {/* Indicador de Báscula - Siempre visible, clickable */}
          {/* Estados: gris (sin bridge) → amarillo (bridge sí, báscula no) → verde (todo OK) */}
          {(() => {
            let bgColor: string, textColor: string, borderColor: string, dotColor: string, label: string, iconColor: string;
            if (!bridgeConnected) {
              bgColor = 'bg-gray-50'; textColor = 'text-gray-400'; borderColor = 'border-gray-200';
              dotColor = 'bg-gray-300'; label = 'Báscula'; iconColor = 'text-gray-400';
            } else if (!scaleConnected) {
              bgColor = 'bg-yellow-50'; textColor = 'text-yellow-600'; borderColor = 'border-yellow-200';
              dotColor = 'bg-yellow-500'; label = 'Sin báscula'; iconColor = 'text-yellow-500';
            } else {
              bgColor = 'bg-indigo-50'; textColor = 'text-indigo-700'; borderColor = 'border-indigo-200';
              dotColor = 'bg-green-500 animate-pulse'; label = scalePort; iconColor = 'text-indigo-600';
            }
            return (
              <button
                onClick={() => { fetchPorts(); setShowScaleConfig(true); }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold border transition-all ${bgColor} ${textColor} ${borderColor} hover:opacity-80`}
                title={
                  !bridgeConnected ? 'Bridge desconectado — clic para configurar'
                  : !scaleConnected ? 'Bridge conectado, báscula no disponible — clic para configurar'
                  : `Báscula conectada en ${scalePort}`
                }
              >
                <Weight size={14} className={iconColor} />
                <span>{label}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>
              </button>
            );
          })()}

          {cashRegister?.status === 'OPEN' ? (
            <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-md text-sm font-bold border border-green-200">
               <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
               Caja Abierta: #{cashRegister.id.slice(-4).toUpperCase()}
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 px-3 py-1.5 rounded-md text-sm font-bold border border-red-200">
               <div className="w-2 h-2 rounded-full bg-red-500"></div>
               Caja Cerrada
            </div>
          )}
          <button onClick={() => setShowSalesModal(true)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-bold text-gray-600 hover:text-gray-900 bg-gray-100/50 hover:bg-gray-200/50 rounded-xl transition-all">
            <Banknote size={16} />
            <span className="hidden sm:inline">
              <span className="bg-gray-300 text-gray-800 text-[10px] px-1.5 py-0.5 rounded ml-1">F7</span> Ventas
            </span>
          </button>
          <button onClick={() => setShowWithdrawalModal(true)} className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-1.5 rounded-md text-sm font-bold transition-colors">
            <Banknote size={16} />
            <span className="bg-gray-300 text-gray-800 text-[10px] px-1.5 py-0.5 rounded ml-1">F8</span> Retiros del Día
          </button>
          {cashRegister?.status === 'OPEN' ? (
            <button onClick={() => setShowCashRegisterModal(true)} className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md text-sm font-bold transition-colors shadow-sm shadow-red-500/20">
              <ArrowDownToLine size={16} />
              Corte Z
            </button>
          ) : (
            <button onClick={() => setShowCashRegisterModal(true)} className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md text-sm font-bold transition-colors shadow-sm shadow-blue-500/20">
              <Plus size={16} />
              Abrir Caja
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* LEFT PANEL: Search and Cart */}
        <div className="flex-1 flex flex-col p-4 border-r border-gray-200">
          {/* Giant Search Bar */}
          <div className="relative mb-4 shadow-sm z-20">
            <Search size={24} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSearchSelectedIndex(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSearchSelectedIndex((prev) => Math.min(prev + 1, products.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSearchSelectedIndex((prev) => Math.max(prev - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  if (products.length > 0 && products[searchSelectedIndex]) {
                    addToCart(products[searchSelectedIndex]);
                  } else {
                    handleBarcodeSearch(searchTerm);
                  }
                }
              }}
              disabled={cashRegister?.status !== 'OPEN'}
              placeholder={cashRegister?.status !== 'OPEN' ? "Debes abrir la caja primero..." : "Escanea aquí o busca un producto por nombre..."}
              className="w-full h-14 pl-12 pr-4 text-lg bg-white border-2 border-gray-200 rounded-xl focus:border-[#007AFF] focus:ring-4 focus:ring-[#007AFF]/10 transition-all outline-none disabled:bg-gray-50 disabled:cursor-not-allowed"
              autoFocus
            />
            {/* Dropdown for search results */}
            {products.length > 0 && searchTerm.length >= 1 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 shadow-xl rounded-xl overflow-hidden z-50 max-h-80 overflow-y-auto">
                {products.map((product, index) => (
                  <button
                    key={product.id}
                    onClick={() => addToCart(product)}
                    onMouseEnter={() => setSearchSelectedIndex(index)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-blue-50 transition-colors flex items-center justify-between ${searchSelectedIndex === index ? 'bg-blue-50/50 border-l-4 border-l-[#007AFF]' : ''}`}
                  >
                    <div>
                      <div className={`font-bold ${searchSelectedIndex === index ? 'text-[#007AFF]' : 'text-gray-800'}`}>{product.name}</div>
                      <div className="text-xs text-gray-500">
                        SKU: {product.sku} • Stock: {product.stock}
                        {product.unit?.name && (
                          <span className="ml-1 text-indigo-500">• {product.unit.name}</span>
                        )}
                        {product.isWeighable && (
                          <span className="ml-1 bg-indigo-100 text-indigo-600 px-1 py-0.5 rounded text-[10px] font-bold">
                            ⚖️ PESABLE
                          </span>
                        )}
                        {promotedProductIds.has(product.id) && (() => {
                          const promos = productPromotions[product.id];
                          if (!promos || promos.length === 0) return null;
                          // Show the first promotion name in the badge
                          const promo = promos[0];
                          const label = promo.description || promo.promotionName;
                          const tooltip = promos.map(p => `${p.promotionName}: ${p.description}`).join('\n');
                          return (
                            <span className="ml-1 bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded text-[10px] font-bold border border-amber-200 shadow-sm" title={tooltip}>
                              ⭐ {label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="font-black text-[#007AFF]">${Number(product.pricePublic).toFixed(2)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Product Grid View ── */}
          {showProductGrid && (
            <div className="mb-4">
              {/* Category filter bar */}
              <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3">
                <button
                  onClick={() => setGridCategoryId('')}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                    !gridCategoryId
                      ? 'bg-[#007AFF] text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Todos
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setGridCategoryId(cat.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                      gridCategoryId === cat.id
                        ? 'bg-[#007AFF] text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
              {/* Product grid */}
              {loadingGrid ? (
                <div className="flex justify-center py-8">
                  <Loader2 size={24} className="animate-spin text-[#007AFF]" />
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-4 xl:grid-cols-5 gap-2 max-h-[300px] overflow-y-auto p-1">
                  {gridProducts.map(product => (
                    <button
                      key={product.id}
                      onClick={() => addToCart(product)}
                      className="flex flex-col items-center gap-1 p-2 bg-white rounded-xl border border-gray-100 hover:border-[#007AFF] hover:shadow-md transition-all active:scale-95"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center text-lg flex-shrink-0">
                        {product.images && product.images.length > 0 && JSON.parse(product.images)[0]
                          ? <img src={JSON.parse(product.images)[0]} alt={product.name} className="w-full h-full rounded-full object-cover" />
                          : '📦'}
                      </div>
                      <p className="text-[10px] font-bold text-gray-700 text-center leading-tight line-clamp-2">{product.name}</p>
                      <p className="text-[10px] font-black text-[#007AFF]">${Number(product.pricePublic).toFixed(2)}</p>
                      {promotedProductIds.has(product.id) && (
                        <span className="text-[8px] bg-amber-50 text-amber-600 px-1 rounded font-bold">⭐ PROMO</span>
                      )}
                    </button>
                  ))}
                  {gridProducts.length === 0 && (
                    <div className="col-span-full text-center py-8 text-gray-400 text-sm">
                      No hay productos en esta categoría
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Cart Items Area */}
          <div className="flex-1 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col">
            {activeItems.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-300">
                <ShoppingCart size={80} className="mb-4 opacity-50" />
                <h2 className="text-2xl font-black uppercase tracking-widest opacity-50">Esperando Productos</h2>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-2">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold sticky top-0">
                    <tr>
                      <th className="p-3 rounded-tl-lg">Descripción</th>
                      <th className="p-3 text-center">Cant.</th>
                      <th className="p-3 text-right">Precio</th>
                      <th className="p-3 text-right">Dto.</th>
                      <th className="p-3 text-right">Importe</th>
                      <th className="p-3 text-center rounded-tr-lg"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activeItems.map((item) => (
                      <tr 
                        key={item.productId} 
                        onClick={() => setSelectedProductId(item.productId)}
                        className={`transition-colors group cursor-pointer ${selectedProductId === item.productId ? 'bg-[#007AFF]/10' : 'hover:bg-blue-50/50'} ${promoFlashProductId === item.productId ? 'promo-flash' : ''}`}
                      >
                        <td className="p-3">
                          <div className="font-bold text-gray-800 text-base flex items-center gap-2">
                            {item.name}
                            {item.unitPrice === item.priceWholesale && (
                              <span className="text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded uppercase">MAYOREO</span>
                            )}
                            {item.isWeighable && (
                              <span className="text-[10px] font-bold bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded uppercase">
                                ⚖️ GRANEL
                              </span>
                            )}
                            {item.sku?.startsWith('REC-') && (
                              <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded uppercase">
                                📱 RECARGA
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 flex items-center gap-2">
                            {item.sku}
                            {item.unitName && (
                              <span className="text-indigo-400">• {item.unitName}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-1 bg-gray-100 rounded-lg p-1 w-max mx-auto">
                            <button onClick={(e) => { e.stopPropagation(); updateQuantity(item.productId, -1); }} className="p-1 hover:bg-white rounded-md text-gray-600 transition-colors">
                              <Minus size={16} />
                            </button>
                            {editingQtyId === item.productId ? (
                              <input 
                                type="number" 
                                autoFocus
                                className="w-12 text-center font-bold bg-white rounded border border-[#007AFF] outline-none hide-arrows"
                                value={editingQtyValue}
                                onChange={(e) => setEditingQtyValue(e.target.value)}
                                onBlur={() => submitQtyEdit(item.productId)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => { 
                                  e.stopPropagation();
                                  if (e.key === 'Enter') submitQtyEdit(item.productId); 
                                }}
                              />
                            ) : (
                              <span 
                                className="w-10 text-center font-bold cursor-text hover:bg-white hover:rounded transition-colors" 
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  setEditingQtyId(item.productId);
                                  setEditingQtyValue(item.quantity.toString());
                                }}
                                title="Doble clic para editar"
                              >
                                {item.quantity}
                              </span>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); updateQuantity(item.productId, 1); }} className="p-1 hover:bg-white rounded-md text-gray-600 transition-colors">
                              <Plus size={16} />
                            </button>
                          </div>
                        </td>
                        <td className="p-3 text-right font-semibold text-gray-600">
                          ${item.unitPrice.toFixed(2)}
                        </td>
                        <td className="p-3 text-right">
                          {editingDiscountId === item.productId ? (
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-[10px] text-gray-400 font-bold">$</span>
                              <input 
                                type="number"
                                autoFocus
                                className="w-16 text-right font-bold bg-white rounded border border-[#007AFF] outline-none text-sm px-1"
                                value={editingDiscountValue}
                                onChange={(e) => setEditingDiscountValue(e.target.value)}
                                onBlur={() => submitDiscountEdit(item.productId)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => { 
                                  e.stopPropagation();
                                  if (e.key === 'Enter') submitDiscountEdit(item.productId);
                                  if (e.key === 'Escape') setEditingDiscountId(null);
                                }}
                                step="0.01"
                                min="0"
                              />
                            </div>
                          ) : (
                            <span 
                              onClick={(e) => { e.stopPropagation(); handleEditDiscount(item.productId); }}
                              className={`cursor-pointer font-bold text-sm px-1.5 py-0.5 rounded transition-colors ${
                                item.discount > 0
                                  ? 'text-[#FF3B30] bg-red-50 hover:bg-red-100'
                                  : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'
                              }`}
                              title="Clic para aplicar descuento"
                            >
                              {item.discount > 0 ? `-$${item.discount.toFixed(2)}` : '—'}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right font-black text-lg text-[#007AFF]">
                          ${item.subtotal.toFixed(2)}
                        </td>
                        <td className="p-3 text-center">
                          <button 
                            onClick={() => removeFromCart(item.productId)}
                            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL: Checkout and Actions */}
        <div className="w-[380px] bg-[#F8FAFC] flex flex-col p-4 border-l border-white shadow-[-4px_0_24px_rgba(0,0,0,0.02)]">
          
          {/* Customer Selection */}
          <div className="bg-white rounded-xl border border-gray-200 p-1 mb-4 shadow-sm">
            {activeCart.selectedCustomer ? (
              <div className="p-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase mb-1">CLIENTE SELECCIONADO</p>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#007AFF]"></div>
                    <p className="font-bold text-gray-800">{activeCart.selectedCustomer.name}</p>
                  </div>
                </div>
                <button 
                  onClick={() => updateActiveCart({ selectedCustomer: null })}
                  className="p-2 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setShowCustomerModal(true)}
                className="w-full flex items-center justify-center gap-2 py-3 text-gray-500 hover:text-[#007AFF] hover:bg-blue-50 transition-colors rounded-lg font-bold"
              >
                <User size={18} />
                <span>Asignar cliente</span>
              </button>
            )}
          </div>

          {/* Totals Box */}
          <div className="flex-1 bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col">
            {/* Promotions */}
            {cartPromotions.length > 0 && (
              <div className="mb-4 space-y-1">
                {cartPromotions.map((p, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg px-3 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />
                      <span className="text-[10px] font-bold text-purple-700 truncate">
                        🏷️ {p.promotionName}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-purple-600 flex-shrink-0 ml-2">
                      -${p.discountAmount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {promotionsLoading && activeItems.length > 0 && cartPromotions.length === 0 && (
              <div className="mb-4 flex items-center gap-2 text-[10px] text-purple-400">
                <Loader2 size={10} className="animate-spin" /> Calculando promociones...
              </div>
            )}

            <div className="flex items-end justify-between mb-6 pb-6 border-b border-gray-100">
              <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">TOTAL</span>
              <span className="text-5xl font-black text-gray-800 tracking-tighter">${grandTotal.toFixed(2)}</span>
            </div>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">PRODUCTOS</span>
                <span className="text-lg font-black text-gray-700">{activeItems.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">CANTIDAD</span>
                <span className="text-lg font-black text-gray-700">{totalItems.toFixed(2)}</span>
              </div>
              {promotionDiscount > 0 && (
                <div className="flex items-center justify-between border-t border-dashed border-gray-200 pt-3">
                  <span className="text-xs font-bold text-purple-600 uppercase tracking-wider">DESC. PROMOS</span>
                  <span className="text-sm font-bold text-purple-600">-${promotionDiscount.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Notes field */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1.5">
                <MessageSquare size={14} className="text-gray-400" />
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">NOTA (opcional)</span>
              </div>
              <textarea
                value={saleNotes}
                onChange={(e) => setSaleNotes(e.target.value)}
                placeholder="Agrega un comentario a la venta..."
                rows={2}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 resize-none outline-none focus:border-[#007AFF] focus:ring-2 focus:ring-[#007AFF]/10 transition-all placeholder:text-gray-300"
              />
            </div>

            <div className="flex items-center gap-6 mb-4 mt-auto justify-center">
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${activeCart.isQuotation ? 'bg-[#007AFF] border-[#007AFF]' : 'border-gray-300 group-hover:border-[#007AFF]'}`}>
                  {activeCart.isQuotation && <Check size={14} className="text-white" />}
                </div>
                <input 
                  type="checkbox" 
                  className="hidden" 
                  checked={activeCart.isQuotation} 
                  onChange={(e) => updateActiveCart({ isQuotation: e.target.checked })} 
                />
                <span className="text-sm font-bold text-gray-600">ES COTIZACIÓN</span>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <button 
                onClick={handleSaveQuotation}
                className="flex flex-col items-center justify-center gap-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 py-3 rounded-xl transition-colors text-gray-700"
              >
                <Save size={20} className="text-gray-500" />
                <span className="text-xs font-black">GUARDAR</span>
                <span className="text-[9px] font-bold text-[#007AFF] bg-blue-50 px-1.5 py-0.5 rounded">F6</span>
              </button>
              <button 
                onClick={() => setShowLoadModal(true)}
                className="flex flex-col items-center justify-center gap-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 py-3 rounded-xl transition-colors text-gray-700"
              >
                <Download size={20} className="text-gray-500" />
                <span className="text-xs font-black">CARGAR</span>
                <span className="text-[9px] font-bold text-[#007AFF] bg-blue-50 px-1.5 py-0.5 rounded">F11</span>
              </button>
            </div>

            <button 
              onClick={() => {
                if (activeCart.isQuotation) handleSaveQuotation();
                else setShowPaymentModal(true);
              }}
              disabled={activeItems.length === 0 || saving || cashRegister?.status !== 'OPEN'}
              className={`w-full py-5 rounded-xl font-black text-xl uppercase tracking-widest transition-all shadow-lg ${
                cashRegister?.status !== 'OPEN'
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                  : activeItems.length === 0 
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none' 
                  : activeCart.isQuotation
                    ? 'bg-[#007AFF] text-white hover:bg-blue-600 hover:shadow-[#007AFF]/30'
                    : 'bg-[#FF3B30] text-white hover:bg-red-600 hover:shadow-[#FF3B30]/30'
              }`}
            >
              {cashRegister?.status !== 'OPEN' ? 'CAJA CERRADA' : saving ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin" /> PROCESANDO...
                </span>
              ) : activeCart.isQuotation ? 'GUARDAR COTIZACIÓN' : 'COBRAR'}
            </button>
          </div>
        </div>
    </div>

      {showLoadModal && (
        <LoadSaleModal 
          isOpen={showLoadModal}
          onClose={() => setShowLoadModal(false)}
          onLoad={handleLoadSale}
        />
      )}

      {showSalesModal && (
        <SalesHistoryModal
          isOpen={showSalesModal}
          onClose={() => setShowSalesModal(false)}
        />
      )}

      {showCashRegisterModal && (
        <CashRegisterModal
          isOpen={showCashRegisterModal}
          onClose={() => {
            setShowCashRegisterModal(false);
            loadCajaStatus();
          }}
        />
      )}

      {/* ⌨️ Modal de atajos de teclado */}
      {showShortcutsModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowShortcutsModal(false)}>
          <div className="bg-white rounded-[20px] w-full max-w-sm shadow-ios-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Keyboard size={18} className="text-gray-500" />
                <h3 className="font-semibold">Teclas Rápidas</h3>
              </div>
              <button onClick={() => setShowShortcutsModal(false)} className="p-2 rounded-lg hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {POS_SHORTCUTS.map((s) => (
                <div key={s.key} className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-700">{s.desc}</span>
                  <kbd className="px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs font-mono font-bold text-gray-600 shadow-sm">
                    {s.key}
                  </kbd>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-200">
              <p className="text-[10px] text-gray-400 text-center">
                Presiona <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-200 rounded text-[10px] font-mono">Ctrl+K</kbd> para abrir esta ayuda
              </p>
            </div>
          </div>
        </div>
      )}

      {showWithdrawalModal && (
        <WithdrawalModal
          isOpen={showWithdrawalModal}
          onClose={() => setShowWithdrawalModal(false)}
        />
      )}

      {showCustomerModal && (
        <CustomerSelectionModal
          isOpen={showCustomerModal}
          onClose={() => setShowCustomerModal(false)}
          onSelect={(customer) => updateActiveCart({ selectedCustomer: customer })}
        />
      )}

      {showRechargeModal && (
        <RechargeModal
          isOpen={showRechargeModal}
          onClose={() => setShowRechargeModal(false)}
          onRecharge={handleRecharge}
        />
      )}          {showPaymentModal && (
            <PaymentModal
              isOpen={showPaymentModal}
              onClose={() => setShowPaymentModal(false)}
              onConfirm={handlePayment}
              grandTotal={grandTotal}
              customerId={activeCart.selectedCustomer?.id}
            />
          )}

          {/* Admin Auth Modal for discount authorization */}
          {showAdminAuthModal && (
            <AdminAuthModal
              isOpen={showAdminAuthModal}
              onClose={() => {
                setShowAdminAuthModal(false);
                setPendingDiscountProductId(null);
              }}
              onSuccess={() => {
                setShowAdminAuthModal(false);
                const productId = pendingDiscountProductId;
                if (productId) {
                  const item = activeItems.find(i => i.productId === productId);
                  if (item) {
                    setEditingDiscountId(productId);
                    setEditingDiscountValue(String(item.discount));
                  }
                }
                setPendingDiscountProductId(null);
              }}
              actionName="aplicar un descuento"
            />
          )}

      {/* ⚖️ Modal de configuración de báscula */}
      {showScaleConfig && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Weight size={20} className="text-indigo-600" />
                Configurar Báscula
              </h3>
              <button
                onClick={() => setShowScaleConfig(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            {/* Estado actual - 3 estados posibles */}
            <div className={`flex items-center gap-3 p-3 rounded-xl mb-4 ${
              !bridgeConnected ? 'bg-gray-50 border border-gray-200'
              : !scaleConnected ? 'bg-yellow-50 border border-yellow-200'
              : 'bg-green-50 border border-green-200'
            }`}>
              <div className={`w-3 h-3 rounded-full ${
                !bridgeConnected ? 'bg-gray-300'
                : !scaleConnected ? 'bg-yellow-500'
                : 'bg-green-500 animate-pulse'
              }`}></div>
              <div>
                <p className="text-sm font-bold text-gray-700">
                  {!bridgeConnected ? 'Bridge desconectado'
                  : !scaleConnected ? 'Bridge conectado, sin báscula'
                  : `Báscula conectada en ${scalePort}`}
                </p>
                <p className="text-xs text-gray-500">
                  {!bridgeConnected ? 'El servicio bridge no está corriendo'
                  : !scaleConnected ? 'Esperando conexión de báscula física'
                  : 'Báscula lista para pesar'}
                </p>
              </div>
            </div>

            {/* Lista de puertos */}
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Puertos disponibles</p>
            <div className="space-y-1 max-h-48 overflow-y-auto mb-4">
              {scalePorts.length === 0 ? (
                <div className="text-sm text-gray-400 py-4 text-center">
                  <p className="mb-1">No se detectaron puertos</p>
                  <p className="text-xs">Verifica que el bridge esté corriendo</p>
                </div>
              ) : (
                scalePorts.map((port) => (
                  <button
                    key={port.path}
                    onClick={() => connectToPort(port.path)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors flex items-center justify-between ${
                      scalePort === port.path
                        ? 'bg-indigo-50 border border-indigo-200 text-indigo-700'
                        : 'hover:bg-gray-50 border border-transparent text-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-bold font-mono">{port.path}</span>
                      {port.manufacturer && (
                        <span className="text-xs text-gray-400">{port.manufacturer}</span>
                      )}
                    </div>
                    {scalePort === port.path && (
                      <Check size={14} className="text-indigo-600" />
                    )}
                  </button>
                ))
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={async () => {
                  try {
                    await fetch('http://localhost:3120/api/disconnect', { method: 'POST' });
                    showFeedback('🔌 Báscula desconectada', 'success');
                  } catch (err) {
                    console.warn('[POS] Error al desconectar báscula:', err);
                  }
                  setShowScaleConfig(false);
                }}
                className="flex-1 px-3 py-2 text-sm font-bold text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
              >
                Desconectar
              </button>
              <button
                onClick={() => { fetchPorts(); }}
                className="px-3 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
              >
                Refrescar
              </button>
              <button
                onClick={() => setShowScaleConfig(false)}
                className="flex-1 px-3 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⚖️ Modal de espera de báscula / ingreso manual */}
      {awaitingScale && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center max-w-sm mx-4 animate-in fade-in zoom-in-95">

            {!manualWeightMode ? (
              /* ── MODO: ESPERANDO BÁSCULA ── */
              <>
                <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-5 animate-pulse">
                  <Weight size={40} className="text-indigo-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  Coloca el producto en la báscula
                </h3>
                <p className="text-5xl font-black text-indigo-600 mb-4 font-mono tracking-tight">
                  {scaleWeight !== null && scaleWeight > 0
                    ? scaleWeight.toFixed(3) + ' kg'
                    : '— — —'}
                </p>
                <p className="text-sm text-gray-400 mb-6">
                  {scaleWeight !== null && scaleWeight > 0
                    ? 'Peso capturado ✓'
                    : 'Esperando peso estable...'}
                </p>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mb-6 overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                    style={{
                      width: scaleWeight !== null && scaleWeight > 0 ? '100%' : '30%',
                      opacity: scaleWeight !== null && scaleWeight > 0 ? 1 : 0.5,
                    }}
                  />
                </div>
                <button
                  onClick={() => setManualWeightMode(true)}
                  className="text-sm font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-4 py-2 rounded-lg transition-colors"
                >
                  Ingresar peso manualmente
                </button>
                <button
                  onClick={() => {
                    setAwaitingScale(null);
                    pendingProductRef.current = null;
                    showFeedback('Captura cancelada', 'error');
                  }}
                  className="block mx-auto mt-3 text-sm text-gray-400 hover:text-gray-600 underline transition-colors"
                >
                  Cancelar
                </button>
              </>
            ) : (
              /* ── MODO: INGRESO MANUAL ── */
              <>
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Weight size={40} className="text-gray-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  Ingresa el peso manualmente
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  {pendingProductRef.current?.name || 'Producto'}
                </p>

                <div className="flex items-center gap-2 bg-gray-50 rounded-xl border border-gray-200 p-1 mb-4">
                  <input
                    type="number"
                    autoFocus
                    step="0.001"
                    min="0.001"
                    value={manualWeight}
                    onChange={(e) => setManualWeight(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const w = parseFloat(manualWeight);
                        if (w > 0 && pendingProductRef.current) {
                          addToCart(pendingProductRef.current, w);
                          setAwaitingScale(null);
                          setManualWeightMode(false);
                          pendingProductRef.current = null;
                          showFeedback(`✓ ${w.toFixed(3)} kg ingresado`, 'success');
                        } else {
                          showFeedback('Ingresa un peso válido', 'error');
                        }
                      }
                    }}
                    placeholder="0.000"
                    className="flex-1 bg-transparent text-center text-4xl font-black text-gray-800 font-mono outline-none hide-arrows"
                  />
                  <span className="text-lg font-bold text-gray-500 pr-3">kg</span>
                </div>

                <button
                  onClick={() => {
                    const w = parseFloat(manualWeight);
                    if (w > 0 && pendingProductRef.current) {
                      addToCart(pendingProductRef.current, w);
                      setAwaitingScale(null);
                      setManualWeightMode(false);
                      pendingProductRef.current = null;
                      showFeedback(`✓ ${w.toFixed(3)} kg ingresado`, 'success');
                    } else {
                      showFeedback('Ingresa un peso válido', 'error');
                    }
                  }}
                  disabled={!manualWeight || parseFloat(manualWeight) <= 0}
                  className="w-full py-3 rounded-xl font-bold text-white bg-[#007AFF] hover:bg-blue-600 transition-colors disabled:bg-gray-200 disabled:text-gray-400 shadow-sm shadow-[#007AFF]/20"
                >
                  Agregar al carrito
                </button>

                <button
                  onClick={() => {
                    setManualWeightMode(false);
                    setManualWeight('');
                  }}
                  className="block mx-auto mt-3 text-sm text-gray-400 hover:text-gray-600 underline transition-colors"
                >
                  Volver a esperar báscula
                </button>
              </>
            )}

          </div>
        </div>
      )}

      {/* Hidden ticket for printing */}
      <div className="hidden">
        {saleToPrint && <SaleTicket sale={saleToPrint} ref={printRef} />}
      </div>
    </div>
  );
}

